import {
  parseResolverResponse,
  type ResolverMutationRequestMetadata,
  type ResolverMutationRequestInput,
  type BaseMutationOptions,
  type IncrementCompanySubscriptionUsageRequestData
} from '@esg/syntiro-api-core';
import { apollo_client_v2 } from '../../App';
import { type FetchResult } from '@apollo/client';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { FUNCTION_NO_DATA_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { auth } from '../../lib/google/firebase';
import { INCREMENT_COMPANY_SUBSCRIPTION_USAGE } from '../mutations/subscription.mutations';
import { validateParams } from '../validators/base.validator';
import { isValidGroupId } from '../validators/group.validator';
import { isValidCompanyId } from '../validators/company.validator';

const SOURCE = 'subscription.service.ts';

/**
 * Increments subscription usage counters for the current company.
 * @param {ResolverMutationRequestMetadata} metadata Metadata containing group_id and company_id.
 * @param {SubscriptionDelta} data Usage increments to apply (e.g. { monthly_data_exports: 1 }).
 */
export async function incrementCompanySubscriptionUsage(
  metadata: ResolverMutationRequestMetadata,
  data: IncrementCompanySubscriptionUsageRequestData
): Promise<void> {
  validateParams(
    { metadata },
    {
      'metadata.group_id': {
        validate: isValidGroupId,
        message: 'Invalid group_id specified.'
      },
      'metadata.company_id': {
        validate: isValidCompanyId,
        message: 'Invalid company_id specified.'
      }
    }
  );

  const trackingId: string = generateUuidV4();

  const input: ResolverMutationRequestInput<
    IncrementCompanySubscriptionUsageRequestData,
    ResolverMutationRequestMetadata,
    BaseMutationOptions
  > = {
    metadata: {
      ...metadata,
      tracking_id: trackingId,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data
  };

  return apollo_client_v2
    .mutate({
      mutation: INCREMENT_COMPANY_SUBSCRIPTION_USAGE,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.incrementCompanySubscriptionUsage) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('incrementCompanySubscriptionUsage'));
      }
      parseResolverResponse<null>(response.data.incrementCompanySubscriptionUsage);
    })
    .catch((err: unknown) => {
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'incrementCompanySubscriptionUsage'),
        { metadata, data },
        trackingId
      );
    });
}
